import { motion } from "framer-motion";
import { CheckCircle2, Clock, ArrowRight, AlertCircle, ShieldCheck, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import Icon from "@/components/Icon";
import { DASHBOARD, IMAGES } from "@/lib/data";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLE = {
  COMPLETE: { c: "text-[#10B981]", b: "border-[#10B981]/40 bg-[#10B981]/10", I: CheckCircle2 },
  "IN PROGRESS": { c: "text-[#FFB800]", b: "border-[#FFB800]/40 bg-[#FFB800]/10", I: Clock },
  "NEXT STEP": { c: "text-[#FF5E1E]", b: "border-[#FF5E1E]/40 bg-[#FF5E1E]/10", I: ArrowRight },
};

const statusFor = (prog, need) => (prog >= need ? "COMPLETE" : need - prog === 1 ? "IN PROGRESS" : "NEXT STEP");

export default function WorkerDashboard() {
  const { user, openAuth } = useAuth();
  const isCandidate = user?.role === "candidate";
  const { nextMove } = DASHBOARD;

  const jp = isCandidate ? (user.journey_progress || 0) : 3;
  const fwDone = isCandidate ? !!user?.courses?.firewatch?.completed : true;
  const progress = isCandidate ? Math.round((jp / 6) * 100) : DASHBOARD.progress;
  const items = isCandidate
    ? [
        { icon: "Flame", label: "Firewatch", status: fwDone ? "COMPLETE" : statusFor(jp, 2) },
        { icon: "HardHat", label: "OSHA / Safety", status: statusFor(jp, 3) },
        { icon: "IdCard", label: "DBIDS / Access Process", status: statusFor(jp, 4) },
        { icon: "FileText", label: "Resume", status: jp >= 1 ? "COMPLETE" : "NEXT STEP" },
        { icon: "Ship", label: "Placement", status: statusFor(jp, 6) },
      ]
    : DASHBOARD.items;

  return (
    <section id="dashboard" className="relative py-24 sm:py-32 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-mono-tech text-[11px] tracking-[0.25em] text-[#FF5E1E] mb-3">[ SEC—WORKER-PORTAL — DIGITAL TRADES PASSPORT ]</div>
            <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl">{isCandidate ? `${user.name.split(" ")[0]}'s maritime career` : "Your maritime career"}</h2>
            <p className="mt-4 max-w-2xl text-[#8FA3B8]">{isCandidate ? "Your live, verified trades passport — progress is saved to your account." : "A preview of the worker portal — sign in to save your own journey, courses, and trades passport."}</p>
          </div>
          {!user && (
            <Button data-testid="dashboard-signin-cta" onClick={() => openAuth("register")} className="rounded-none bg-[#FF5E1E] hover:bg-[#ff7038] text-[#0B0F14] font-display font-bold uppercase h-11">
              <LogIn className="mr-2" size={16} /> Create My Passport
            </Button>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Progress + readiness */}
          <div className="lg:col-span-2 bg-[#10161F] border border-white/10 p-6 sm:p-8">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="font-mono-tech text-[10px] tracking-[0.2em] text-[#8FA3B8]">READINESS METER</div>
                <div className="font-display font-black text-5xl sm:text-6xl mt-1">
                  {progress}<span className="text-[#FF5E1E]">%</span>
                </div>
                <div className="font-display font-bold uppercase text-sm text-[#8FA3B8]">Shipyard Ready</div>
              </div>
              <div className="flex items-center gap-2 border border-[#10B981]/40 bg-[#10B981]/10 px-3 py-1.5">
                <ShieldCheck size={16} className="text-[#10B981]" />
                <span className="font-mono-tech text-[10px] tracking-[0.15em] text-[#10B981]">{isCandidate && user.workforce_ready ? "VERIFIED PROFILE" : isCandidate ? "IN PROGRESS" : "DEMO PREVIEW"}</span>
              </div>
            </div>

            <div className="mt-5 h-2.5 bg-[#0B0F14] overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${progress}%` }}
                viewport={{ once: true }}
                transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                className="h-full bg-gradient-to-r from-[#FF5E1E] to-[#FFB800]"
              />
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {items.map((it, i) => {
                const st = STATUS_STYLE[it.status];
                return (
                  <motion.div
                    key={i}
                    data-testid={`readiness-item-${i}`}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.06 }}
                    className="flex items-center justify-between bg-[#0B0F14] border border-white/10 px-4 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <Icon name={it.icon} size={18} className="text-[#8FA3B8]" />
                      <span className="text-sm font-medium">{it.label}</span>
                    </div>
                    <span className={`flex items-center gap-1.5 font-mono-tech text-[9px] tracking-[0.12em] px-2 py-1 border ${st.b} ${st.c}`}>
                      <st.I size={12} /> {it.status}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Next move */}
          <div className="bg-[#182230] border border-white/10 p-6 sm:p-8 flex flex-col">
            <div className="font-mono-tech text-[10px] tracking-[0.2em] text-[#FF5E1E]">YOUR NEXT CAREER MOVE</div>
            <h3 className="font-display font-black uppercase text-2xl mt-2">{nextMove.role}</h3>
            <img src={IMAGES.portraitPipe} alt="Structural fitter" className="mt-4 h-36 w-full object-cover grayscale contrast-125" />

            <div className="mt-5 space-y-4 text-sm flex-1">
              <Block label="Completed" items={nextMove.completed} tone="text-[#10B981]" I={CheckCircle2} />
              <Block label="Missing requirements" items={nextMove.missing} tone="text-[#FFB800]" I={AlertCircle} />
            </div>

            <Button asChild className="mt-5 w-full rounded-none bg-[#FF5E1E] hover:bg-[#ff7038] text-[#0B0F14] font-display font-bold uppercase h-11 group">
              <a href="#apply">Advance My Career <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} /></a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Block({ label, items, tone, I }) {
  return (
    <div>
      <div className="font-mono-tech text-[9px] tracking-[0.2em] text-[#8FA3B8] mb-2">{label.toUpperCase()}</div>
      <ul className="space-y-1.5">
        {items.map((x, i) => (
          <li key={i} className="flex items-center gap-2 text-[#B8C4D0]">
            <I size={14} className={tone} /> {x}
          </li>
        ))}
      </ul>
    </div>
  );
}
